import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/providers/auth-provider'
import { PageLoader } from '@/components/ui/loading-spinner'

interface ProtectedRouteProps {
  children: React.ReactNode
  requirePremium?: boolean
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requirePremium = false,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return <PageLoader />
  }

  if (!isAuthenticated || !user) {
    // Redirect to login with return path
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace
      />
    )
  }

  if (requirePremium && !user.is_premium) {
    // Redirect to billing page if premium is required
    return (
      <Navigate
        to="/dashboard/billing"
        state={{
          from: location,
          message: 'This feature requires a premium subscription.'
        }}
        replace
      />
    )
  }

  return <>{children}</>
}

// Hook to check if user has premium access
export const usePremiumAccess = () => {
  const { user } = useAuth()
  return user?.is_premium ?? false
}

// HOC for premium features
export const withPremiumAccess = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback?: React.ComponentType<P>
) => {
  const PremiumComponent = (props: P) => {
    const hasPremiumAccess = usePremiumAccess()

    if (!hasPremiumAccess) {
      if (fallback) {
        const FallbackComponent = fallback
        return <FallbackComponent {...props} />
      }
      return (
        <div className="p-4 border border-muted rounded-lg">
          <p className="text-muted-foreground">
            This feature requires a premium subscription.
          </p>
        </div>
      )
    }

    return <WrappedComponent {...props} />
  }

  return PremiumComponent
}
